import { createContext, ReactElement, ReactNode, useContext } from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import {
  schema,
  Schema,
} from '../components/HirePlanForm/data/schemaDependent';
import { useWebSocketAssinatura } from '../hooks/useWebSocketAssinatura';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { useError } from '../stores/useError';
import { useHirePlanFormStepStore } from '../stores/useHirePlanFormStepStore';
import { useLinkStore } from '../stores/useLinkStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { useResponseStore } from '../stores/useResponseStore';

type PaymentFormSchema = Schema;

interface HirePlanFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  goToNextForm: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
  getValues: UseFormGetValues<PaymentFormSchema>;
  trigger: UseFormTrigger<PaymentFormSchema>;
  navigateToProposta: () => void;
}

const HirePlanFormContext = createContext({} as HirePlanFormContextProps);
const Prefeitura_Ilha_cod = Number(
  process.env.REACT_APP_CD_PREFEITURA_ILHABELA ?? '28229',
);
const Prefeitura_Ilha_cod_dep = Number(
  process.env.REACT_APP_CD_PREFEITURA_ILHABELA_DEP ?? '28236',
);
interface HirePlanFormProviderProps {
  children: ReactNode;
}

export function UpdateDependentProvider({
  children,
}: HirePlanFormProviderProps): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const goToNextStep = useHirePlanFormStepStore(state => state.goToNextStep);
  const navigate = useNavigate();
  const storedResponse = useResponseStore(x => x.response);
  const { aguardarAssinatura } = useWebSocketAssinatura();

  const setLoading = useLoadingStore(state => state.setLoading);
  const setError = useError(state => state.setError);
  const setTitle = useError(state => state.setErrorTitle);
  const setText = useError(state => state.setErrorText);
  const setResponse = useResponseStore(x => x.setResponse);
  const setLinkData = useLinkStore(x => x.setLinkData);

  interface CheckoutData {
    token: string;
    cpf?: string;
    verifica: boolean;
    nomeTitular?: string;
    dataNascimento?: string;
    emailTitular?: string;
    cep?: string;
    idUf?: string;
    endereco?: string;
    numeroCasa?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    celular?: string;
    nomeMae?: string;
    matricula?: string;
    idSexo?: string;
    idEstadoCivil?: string;
    formaPagamento?: object;
    proposta: string;
    valor_Mensalidade: number;
    vencimentoBoleto?: string;
    vencimentoDebito?: string;
    vencimentoConsignado?: string;
    qtdVidas: string;
    dependentes?: object;
    dependenteSamePlan: boolean;
    idBanco?: string;
    conta?: string;
    agencia?: string;
    operacao?: string;
    primeiraBoleto?: boolean;
    responsavelFinanceiro: object;
    idOrgaoExpedidor: number;
    idOrgaoExpedidorUf: number;
    idFontePagadora: number;
    orgao: number;
  }

  const calculateDueDate = (): string => {
    const today = new Date();
    today.setMonth(today.getMonth() + 1);

    today.setDate(1);

    return today.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const getMonthAndYear = (): string => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() + 2);
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const year = currentDate.getFullYear();

    return `${year}${month}`;
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<PaymentFormSchema>({
    resolver: zodResolver(schema),
  });

  const goToNextForm: SubmitHandler<PaymentFormSchema> = () => {
    goToNextStep();
  };

  const normalizePlanValue = (value?: string): string =>
    value?.replace('R$', '').trim().replace(',', '.') || '4.9';

  const getCodigoParceiro = (): number => {
    const codigoParceiro = Number(
      initialPlanData?.vendedorS4eId ??
        initialPlanData?.vendedorId ??
        initialPlanData?.codVendedor ??
        initialPlanData?.vendedor?.nu_cd_vendedor_s4_e ??
        initialPlanData?.vendedor?.id_vendedor,
    );

    if (!codigoParceiro) {
      throw new Error('Token da pagina sem vendedor vinculado.');
    }

    return codigoParceiro;
  };

  const getTipoParentesco = (
    parentage: string | number | undefined,
    isIlhaBela: boolean,
  ): number => {
    const parentageId = Number(parentage);
    const tipoLegado =
      parentageId === 10 || parentageId === 3
        ? 3
        : parentageId === 11 || parentageId === 4
        ? 4
        : 10;

    if (!isIlhaBela) {
      return tipoLegado;
    }

    const parentesco = initialPlanData?.listaParentescos?.find(
      item => Number(item?.id_parentesco) === parentageId,
    );

    return Number(parentesco?.cd_parentesco) || tipoLegado;
  };

  const getAvailablePlanPaymentOption = () => {
    const individualPayments =
      initialPlanData?.produtoComercial?.formasPagamentoIndividual ??
      initialPlanData?.formasPagamentoIndividual ??
      [];
    const planPayments = initialPlanData?.formasPagamento ?? [];

    return (
      individualPayments.find(
        paymentOption => Number(paymentOption?.id_meiopagto_if) !== 0,
      ) ??
      planPayments.find(
        paymentOption => Number(paymentOption?.id_meiopagto_if) !== 0,
      )
    );
  };

  const getAvailablePlanPaymentData = () => {
    const paymentOption = getAvailablePlanPaymentOption();
    const paymentMethod = paymentOption?.meioPagamentoIndividual;
    const group =
      Number(paymentMethod?.cd_gmeiopagto ?? paymentOption?.cd_gmeiopagto) || 4;
    const id =
      Number(
        paymentOption?.id_meiopagto_if ??
          paymentMethod?.id_meiopagto ??
          paymentMethod?.cd_codmeiopagto,
      ) || 3;

    return {
      gpPagto: group,
      idPagto: id,
    };
  };

  const getTipoPagamento = (): number => {
    const paymentOption = getAvailablePlanPaymentOption();
    const paymentMethod = paymentOption?.meioPagamentoIndividual;

    return Number(paymentMethod?.cd_codmeiopagto) || 529;
  };

  const showApiError = (error: any): void => {
    const errorData = error?.response?.data;
    const apiMessage =
      typeof errorData === 'string'
        ? errorData
        : errorData?.error ||
          errorData?.message ||
          errorData?.mensagem ||
          'Não foi possível concluir a inclusão dos dependentes.';
    const apiCode = errorData?.code;

    alert(apiCode ? `Erro ${apiCode}: ${apiMessage}` : apiMessage);
  };

  const parsePlanValue = (value?: string): number => {
    const parsed = parseFloat(
      value?.replace('R$', '').trim().replace(',', '.') || '0',
    );

    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getIlhabelaTitularPlanValue = (
    selectedPlanValue?: string,
    originalTitularPlanValue?: string,
  ): string => {
    const planValue = parseFloat(normalizePlanValue(selectedPlanValue));
    const originalPlanValue = parsePlanValue(originalTitularPlanValue);
    const discountedValue = Math.max(0, planValue - originalPlanValue);

    return discountedValue.toFixed(2);
  };

  const normalizeCpf = (value?: string): string =>
    value?.replace(/\D/g, '') || '';

  const normalizeText = (value?: string): string =>
    value
      ?.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase() || '';

  const normalizeDateForCompare = (value?: string): string => {
    if (!value) {
      return '';
    }

    const dateOnly = value.split(' ')[0].trim();
    const isoDateMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day}/${month}/${year}`;
    }

    return dateOnly;
  };

  const getDependentCpf = (dependent: any): string =>
    normalizeCpf(dependent?.cpf || dependent?.numeroCpfDependente);

  const getDependentName = (dependent: any): string =>
    normalizeText(dependent?.name || dependent?.nomeDependente);

  const getDependentBirthDate = (dependent: any): string =>
    normalizeDateForCompare(dependent?.birthDate || dependent?.dataNascimento);

  const isAlreadyRegisteredDependent = (
    dependent: any,
    registeredDependents: any[],
  ): boolean => {
    const dependentCpf = getDependentCpf(dependent);
    const dependentName = getDependentName(dependent);
    const dependentBirthDate = getDependentBirthDate(dependent);

    return registeredDependents.some(registeredDependent => {
      const registeredCpf = getDependentCpf(registeredDependent);
      if (dependentCpf && registeredCpf) {
        return dependentCpf === registeredCpf;
      }

      const registeredName = getDependentName(registeredDependent);
      const registeredBirthDate = getDependentBirthDate(registeredDependent);

      return (
        Boolean(dependentName) &&
        Boolean(dependentBirthDate) &&
        dependentName === registeredName &&
        dependentBirthDate === registeredBirthDate
      );
    });
  };

  const getNewDependentsForInclude = (data: PaymentFormSchema) => {
    const dependents = data.dependents ?? [];
    const registeredDependents = [
      ...(data.Associado.selecionado?.dependentes ?? []),
      ...(data.Associado.dependentes ?? []),
    ].filter(
      dependent =>
        Number(dependent?.codigoGrauParentesco ?? dependent?.parentage) !== 1,
    );

    if (registeredDependents.length === 0) {
      return dependents;
    }

    return dependents.filter(
      dependent =>
        !isAlreadyRegisteredDependent(dependent, registeredDependents),
    );
  };

  const parseCurrencyValue = (value?: string): number => {
    const parsed = parseFloat(
      value?.replace('R$', '').trim().replace(',', '.') || '0',
    );

    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatDate = (date: string) => {
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  };
  const vigencia = new Date();
  vigencia.setDate(vigencia.getDate() + 30);

  const gerarDadosCheckout = async (
    data: PaymentFormSchema,
    MonthYear: number,
    flowId: string,
  ) => {
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const propostaNr = String(Math.floor(Math.random() * 1000000000));
    const dependents = getNewDependentsForInclude(data);
    const isPrefeituraIlha = [
      Prefeitura_Ilha_cod,
      Prefeitura_Ilha_cod_dep,
    ].includes(Number(data.Associado.selecionado.codigoDaEmpresa));
    const dependentesSelecionados =
      data.Associado.selecionado?.dependentes ?? [];
    const hasContratoDependente = Boolean(
      data.Associado.selecionado?.codigoContratoDependente,
    );
    const hasDependenteSelecionado =
      dependentesSelecionados.some(
        dependent => Number(dependent.codigoGrauParentesco) !== 1,
      ) || hasContratoDependente;
    const titularSelecionado = dependentesSelecionados.find(
      dependent =>
        dependent.codigoGrauParentesco === 1 &&
        dependent.numeroCpfDependente?.replace(/[.-]/g, '') ===
          data.Associado.cpf?.replace(/[.-]/g, ''),
    );
    const valorTitularContratoOriginal =
      titularSelecionado?.valorPlano ??
      dependentesSelecionados.find(
        dependent => Number(dependent.codigoGrauParentesco) === 1,
      )?.valorPlano;
    const planoAtualTitular =
      titularSelecionado?.codigoPlano ??
      data.Associado.selecionado.dependentes?.[0]?.codigoPlano;
    const changePlan =
      String(data.Associado.plano) !== String(planoAtualTitular);
    let dependentesComTitular = [];

    const titularAssociado = {
      tipo: 1,
      nome: data.Associado.name,
      cpf: data.Associado.cpf?.replace(/[.-]/g, ''),
      sexo:
        (titularSelecionado?.sexo ?? data.Associado.selecionado?.sexo) == '1'
          ? 0
          : 1,
      plano: data.Associado.plano,
      planoValor: normalizePlanValue(data?.Associado?.plano_vl),
      numeroProposta: propostaNr,
      carenciaAtendimento: 0,
      rcaId: 0,
      cns: data.Associado.selecionado?.cns || '',
      estadoCivil: data.Associado.civilStatus ?? '',
      escolaridade: data.Associado.educationLevel ?? '',
      parentescoOriginal: Number(titularSelecionado?.codigoGrauParentesco ?? 1),
      cd_orientacao_sexual: 0,
      OutraOrientacaoSexual: '',
      cd_ident_genero: 0,
      OutraIdentidadeGenero: '',
      idExterno: '',
      nomeMae:
        titularSelecionado?.nomeMaeDependente ??
        data.Associado.selecionado?.nomeMaeDependente ??
        '',
      MMYYYY1Pagamento: MonthYear,
      numeroCarteira: '',
      observacaoUsuario: '',
      dataNascimento: formatDate(data.Associado.birthDate),
      funcionarioCadastro: 62181,
      dataCadastroLoteContrato: '',
    };

    const buildNovosDependentes = () =>
      dependents.map(dependent => ({
        tipo: getTipoParentesco(dependent.parentage, isPrefeituraIlha),
        nome: dependent.name,
        cpf: dependent.cpf?.replace(/[.-]/g, '') ?? '',
        sexo: dependent.gender == '1' ? 0 : 1,
        plano: dependent.plano
          ? parseFloat(dependent.plano)
          : data.Associado.plano,
        planoValor: normalizePlanValue(
          dependent.plano_vl || data?.Associado?.plano_vl,
        ),
        cns: dependent.cns || '',
        numeroProposta: propostaNr,
        carenciaAtendimento: 0,
        rcaId: 0,
        estadoCivil: dependent.civilStatus,
        escolaridade: dependent.educationLevel,
        parentescoOriginal: Number(dependent.parentage),
        cd_orientacao_sexual: 0,
        OutraOrientacaoSexual: '',
        cd_ident_genero: 0,
        OutraIdentidadeGenero: '',
        idExterno: '',
        nomeMae: dependent.mothersName,
        MMYYYY1Pagamento: MonthYear,
        numeroCarteira: '',
        observacaoUsuario: '',
        dataNascimento: formatDate(dependent.birthDate),
        funcionarioCadastro: 62181,
        dataCadastroLoteContrato: '',
      }));

    const buildDependentesComTitular = () => [
      {
        ...titularAssociado,
        nomeMae: data.Associado.name,
      },
      ...buildNovosDependentes(),
    ];

    const buildPrimeiraInclusaoPrefeitura = () => {
      if (changePlan) {
        return [
          {
            ...titularAssociado,
            planoValor: getIlhabelaTitularPlanValue(
              data?.Associado?.plano_vl,
              valorTitularContratoOriginal,
            ),
          },
          ...buildNovosDependentes(),
        ];
      }

      if (dependents.length === 0) {
        return [titularAssociado];
      }

      let titular = null;

      const conjuge = dependents.find(
        x => x.parentage === '10' || x.parentage === '3',
      );

      if (conjuge) {
        titular = conjuge;
      } else {
        const filhos = dependents.filter(
          x => x.parentage === '11' || x.parentage === '4',
        );

        if (filhos.length > 0) {
          titular = filhos.reduce((maisVelho, atual) => {
            return new Date(atual.birthDate) < new Date(maisVelho.birthDate)
              ? atual
              : maisVelho;
          });
        } else {
          titular = dependents[0];
        }
      }

      const dependentesSemTitular = titular
        ? dependents.filter(x => x !== titular)
        : dependents;

      return [
        {
          tipo: 1,
          nome: titular.name,
          cpf: titular.cpf?.replace(/[.-]/g, '') ?? '',
          sexo: titular.gender == '1' ? 0 : 1,
          plano: data.Associado.plano,
          planoValor: normalizePlanValue(data?.Associado?.plano_vl),
          numeroProposta: propostaNr,
          carenciaAtendimento: 0,
          rcaId: 0,
          cns: data.Associado.selecionado?.cns || '',
          estadoCivil: titular.civilStatus,
          escolaridade: titular.educationLevel,
          parentescoOriginal: Number(titular.parentage),
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: titular.mothersName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(titular.birthDate),
          funcionarioCadastro: 62181,
          dataCadastroLoteContrato: '',
        },
        ...(dependentesSemTitular?.map(dependent => ({
          tipo: getTipoParentesco(dependent.parentage, isPrefeituraIlha),
          nome: dependent.name,
          cpf: dependent.cpf?.replace(/[.-]/g, '') ?? '',
          sexo: dependent.gender == '1' ? 0 : 1,
          plano: data.Associado.plano,
          planoValor: normalizePlanValue(
            dependent.plano_vl || data?.Associado?.plano_vl,
          ),
          cns: dependent.cns || '',
          numeroProposta: propostaNr,
          carenciaAtendimento: 0,
          rcaId: 0,
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          estadoCivil: dependent.civilStatus,
          escolaridade: dependent.educationLevel,
          parentescoOriginal: Number(dependent.parentage),
          nomeMae: dependent.mothersName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(dependent.birthDate),
          funcionarioCadastro: 62181,
          dataCadastroLoteContrato: '',
        })) || []),
      ];
    };
    if (isPrefeituraIlha) {
      const hoje = new Date();
      const mesesParaVigencia = hoje.getDate() > 15 ? 2 : 1;
      vigencia.setFullYear(
        hoje.getFullYear(),
        hoje.getMonth() + mesesParaVigencia,
        1,
      );

      dependentesComTitular = hasDependenteSelecionado
        ? buildNovosDependentes()
        : buildPrimeiraInclusaoPrefeitura();
    } else {
      dependentesComTitular = buildDependentesComTitular();
    }

    const orgaoExp = initialPlanData.listaOrgaosExpedidor.find(
      x => x.id_oe === data.Associado.selecionado?.orgao,
    );
    const calculaValorAtual = dependentesSelecionados.reduce(
      (total, dependent) => total + parseCurrencyValue(dependent.valorPlano),
      0,
    );
    const calculaValor =
      dependents.length *
      parseFloat(normalizePlanValue(data.Associado.plano_vl));
    const valorPlanoSelecionado = parseFloat(
      normalizePlanValue(data.Associado?.plano_vl),
    );
    const quantidadeVidasContrato =
      dependentesSelecionados.length + dependents.length;
    const valorTotalContratoBase = changePlan
      ? quantidadeVidasContrato * valorPlanoSelecionado
      : calculaValor + calculaValorAtual;
    const valorTotalContrato =
      isPrefeituraIlha && !hasDependenteSelecionado
        ? dependentesComTitular.reduce(
            (total, dependent) =>
              total + parseCurrencyValue(String(dependent.planoValor)),
            0,
          )
        : valorTotalContratoBase;
    const endereco = await axios.post(
      `${process.env.REACT_APP_URL_BASE}/api/redeatendimento/Endereco?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cep=${data.Associado.selecionado.cep}`,
    );
    const contatos = [];
    if (data.Associado.selecionado.contatos) {
      const celular = data.Associado.selecionado.contatos.find(
        x => x.tipo === 'Celular',
      );
      const email = data.Associado.selecionado.contatos.find(
        x => x.tipo === 'E-mail',
      );
      if (celular) {
        const newContato = {
          tipo: 8,
          dado: celular.descricaoContato,
        };
        contatos.push(newContato);
      }
      if (email) {
        const newContato = {
          tipo: 50,
          dado: email.descricaoContato,
        };
        contatos.push(newContato);
      }
    }

    const checkoutData = {
      token: token,
      WsId: flowId,
      dadosNovo: {
        changePlan: changePlan,
        hasNewDependente: dependents.length > 0,
        parcelaRetidaComissao: '0',
        incluirMensalidades: '1',
        parceiro: {
          codigo: getCodigoParceiro(),
          tipoCobranca: 1,
          adesionista: 0,
          maxMensalidadeId: '1',
        },
        formaPagamento: getAvailablePlanPaymentData(),
        responsavelFinanceiro: {
          ...(isPrefeituraIlha && hasDependenteSelecionado
            ? {
                codigo:
                  data.Associado.selecionado?.codigoContratoDependente ??
                  data.Associado.selecionado?.codigo,
              }
            : {}),
          codigoContrato: isPrefeituraIlha
            ? Prefeitura_Ilha_cod_dep
            : initialPlanData.produtoComercial.formasPagamentoIndividual
                .cd_cod_contrato_s4_e,
          nome: data.Associado.name,
          nomeMae: data.Associado.selecionado?.nomeMaeDependente ?? '',
          dataNascimento: formatDate(data.Associado.birthDate),
          cpf: data.Associado.cpf?.replace(/[.-]/g, ''),
          sexo: data.Associado.selecionado?.sexo == '1' ? 0 : 1,
          estadoCivil: data.Associado.civilStatus ?? '',
          escolaridade: data.Associado.educationLevel ?? '',
          identidadeNumero: data.Associado.selecionado?.rg,
          identidadeOrgaoExpeditor: orgaoExp?.sigla || '',
          enderecoBoleto: '',
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          agencia: 0,
          agencia_dv: 0,
          conta: 0,
          conta_dv: 0,
          contaOperacao: 0,
          matricula: data.Associado.matricula,
          orgao: data.Associado.departamentoId,
          dataApresentacao: '',
          diaVencimento: '01',
          tipoPagamento: getTipoPagamento(),
          origemVenda: 13,
          observacaoAssociado: '',
          codSistemaExterno: '',
          dataAssinaturaContrato: vigencia.toISOString().split('T')[0],
          numeroProposta: propostaNr,
          senhaAssociado: '',
          endereco: {
            cep: data.Associado.selecionado?.cep,
            logradouro: endereco.data.dados.Logradouro,
            numero: String(data.Associado.selecionado?.numero || 0),
            complemento: data.Associado.selecionado?.complemento || '',
            bairro: String(endereco.data.dados.IdBairro),
            municipio: String(endereco.data.dados.IdMunicipio),
            uf: String(endereco.data.dados.IdUf),
            descricaoUf: endereco.data.dados.Uf,
          },
          fl_AlteraSituacao: 16,
          contatoResponsavelFinanceiro: contatos,
        },
        dependente: dependentesComTitular,
        contatoDependente: [],
      },
      empresa: '',
      send: {
        nome: data.Associado.name,
        cpf: data.Associado.cpf,
        matricula: data.Associado.matricula,
        valor: String(valorTotalContrato),
      },
    };
    return checkoutData;
  };

  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    const adesao = calculateDueDate();
    const MonthYear = getMonthAndYear();
    const formatDate = (date: string) => {
      const [day, month, year] = date.split('/');
      return `${year}-${month}-${day}`;
    };

    const dependents = getNewDependentsForInclude(data);

    if ((data.dependents?.length ?? 0) > 0 && dependents.length === 0) {
      alert('Os dependentes informados já constam no contrato.');
      return;
    }

    const codigoEmpresaSelecionada = Number(
      data.Associado.selecionado?.codigoDaEmpresa,
    );
    const isPrefeituraIlhaSelecionada = [
      Prefeitura_Ilha_cod,
      Prefeitura_Ilha_cod_dep,
    ].includes(codigoEmpresaSelecionada);

    const newData = {
      token: initialPlanData.token,
      dados: {
        parceiro: {
          codigo: getCodigoParceiro(),
          adesionista: 0,
        },
        responsavelFinanceiro: {
          codigo: data.Associado.id,
          dataAssinaturaContrato: formatDate(adesao),
        },
        dependente: dependents.map(dependent => ({
          tipo: getTipoParentesco(
            dependent.parentage,
            isPrefeituraIlhaSelecionada,
          ),
          nome: dependent.name ?? '',
          cpf: dependent.cpf?.replace(/[.-]/g, '') ?? '',
          sexo: dependent.gender === '1' ? 0 : 1,
          plano: dependent.plano
            ? parseFloat(dependent.plano)
            : data.Associado.plano,
          planoValor: normalizePlanValue(
            dependent.plano_vl || data?.Associado?.plano_vl,
          ),
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          estadoCivil: dependent.civilStatus,
          escolaridade: dependent.educationLevel,
          parentescoOriginal: Number(dependent.parentage),
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: dependent.mothersName ?? '',
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(dependent.birthDate),
          funcionarioCadastro: 0,
          dataCadastroLoteContrato: '',
        })),
        contatoDependente: [],
      },
    };
    const codigoContratoS4e = Number(
      initialPlanData.produtoComercial.formasPagamentoIndividual
        .cd_cod_contrato_s4_e,
    );
    setLoading(true);
    if (codigoContratoS4e === 27855 && !isPrefeituraIlhaSelecionada) {
      const Url = process.env.REACT_APP_OG_POST_INCLUSAO_DEPENDENTE;
      try {
        const response = await axios.post(Url, newData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const responseData = response.data;

        setLoading(false);
        setError(false);
        setResponse(responseData);
        goToNextStep();
        navigate('/proposta', { state: responseData });
      } catch (error: any) {
        setLoading(false);
        setError(true);

        const errorData = error.response?.data;

        showApiError(error);

        if (errorData?.code) {
          setTitle(errorData.code);
          setText(errorData.error);
        } else {
          setText(errorData?.message || 'Erro inesperado');
        }
      }
    } else {
      const Url = `${process.env.REACT_APP_OG_API_URL}/include/dependentes`;
      const flowId = uuid();
      try {
        const checkoutData = await gerarDadosCheckout(
          data,
          Number(MonthYear),
          flowId,
        );
        const response = await axios.post(Url, checkoutData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data.LinkAssinatura) {
          setLinkData(response.data.LinkAssinatura);
          handleSubmit(goToNextForm)();

          await aguardarAssinatura({
            flowId,
            onConfirm: async payload => {
              const responseData =
                payload?.RetornoInclusao ??
                payload?.[1]?.RetornoInclusao ??
                payload;

              setLoading(false);
              setError(false);
              setResponse(responseData);
              navigate('/proposta', { state: responseData });
            },
          });
          return;
        } else {
          alert('Erro ao gerar link de assinatura. Tente novamente.');
          setLoading(false);
          return;
        }
      } catch (error: any) {
        setLoading(false);
        setError(true);

        const errorData = error.response?.data;

        showApiError(error);

        if (errorData?.code) {
          setTitle(errorData.code);
          setText(errorData.error);
        } else {
          setText(errorData?.message || 'Erro inesperado');
        }
      }
    }
  };

  const navigateToProposta = () => {
    if (storedResponse) {
      navigate('/proposta', { state: storedResponse });
    }
  };

  return (
    <HirePlanFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        goToNextForm,
        errors,
        clearErrors,
        unregister,
        setValue,
        getValues,
        trigger,
        navigateToProposta,
      }}
    >
      {children}
    </HirePlanFormContext.Provider>
  );
}

export function useUpdateDependentForm(): HirePlanFormContextProps {
  return useContext(HirePlanFormContext);
}
